import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import amplitude from '@/amplitude';
import type { BillingPeriod, SubscriptionPlansData } from '@/features/subscription/types';
import { vestiaireAccessNeedsRefresh } from '@/features/subscription/vestiaireAccess';
import { mapBackendPeriodicityToFrontend, mapBackendSubscriptionPlanToData } from '@/shared/api/mappers/subscription';
import {
  SubscriptionPlanPeriodicity,
  useCreateStripeCheckoutSessionMutation,
  useCreateStripePortalSessionMutation,
  useGetAvailableSubscriptionPlans,
  useGetCanStartFreeTrialQuery,
  useGetCurrentUserAnalysisQuotasQuery,
  useGetCurrentUserSubscription,
  useGetCurrentUserVeilleQuotasQuery,
  useGetCurrentUserVestiaireAccessQuery,
  useSelectPlanDuringTrialMutation,
} from '@/shared/hooks/queries/generated';

export const useSubscriptionPlansData = (billingPeriod: BillingPeriod) => {
  const queryClient = useQueryClient();

  // Queries
  const { data: plansData, isLoading: plansLoading } = useGetAvailableSubscriptionPlans();
  const { data: currentSubscriptionData, isLoading: subscriptionLoading } = useGetCurrentUserSubscription(
    {},
    {
      refetchInterval: 5000,
    },
  );
  // Éligibilité à l'essai gratuit — tranchée par le SERVEUR (abonnement passé
  // OU carte déjà utilisée pour un essai → false)
  const { data: canTrialData } = useGetCanStartFreeTrialQuery(undefined, { staleTime: 60_000, retry: 1 });

  // Mutations
  const createCheckoutSession = useCreateStripeCheckoutSessionMutation();

  // Mutation pour créer une session de portail Stripe
  const createPortalSession = useCreateStripePortalSessionMutation();

  // Bascule directe pendant l'essai : fin d'essai immédiate + débit de la carte
  const selectPlanMutation = useSelectPlanDuringTrialMutation();

  // Handle subscription
  const handleSubscribe = useCallback(
    async (planId: string, periodicity: SubscriptionPlanPeriodicity) => {
      try {
        const billingPeriodParam = periodicity === 'monthly' ? 'monthly' : 'yearly';

        const result = await createCheckoutSession.mutateAsync({
          input: {
            planId,
            periodicity,
            successUrl: `${window.location.origin}/subscription?success=true&billing_period=${billingPeriodParam}`,
            cancelUrl: `${window.location.origin}/subscription?billing_period=${billingPeriodParam}`,
          },
        });

        if (result.createStripeCheckoutSession.url) {
          // Tracker l'événement de début de souscription
          amplitude.track('Paiement Started', { planId, periodicity });

          window.location.href = result.createStripeCheckoutSession.url;
        }
      } catch (error) {
        console.error('Error creating checkout session:', error);
        throw error;
      }
    },
    [createCheckoutSession],
  );

  // Handle subscription cancel
  const handleCancel = useCallback(async () => {
    try {
      const result = await createPortalSession.mutateAsync({
        input: {
          type: 'subscriptionCancel',
          returnUrl: window.location.href,
        },
      });

      if (result.createStripePortalSession.url) {
        window.location.href = result.createStripePortalSession.url;
      }
    } catch (error) {
      console.error('Error creating cancellation portal session:', error);
      throw error;
    }
  }, [createPortalSession]);

  const selectPlanDuringTrial = useCallback(
    async (planId: string, periodicity: SubscriptionPlanPeriodicity) => {
      await selectPlanMutation.mutateAsync({ input: { planId, periodicity } });
      amplitude.track('Trial Converted', { planId, periodicity });
      // L'abonnement, les quotas mensuels, les slots de veille et l'accès Vestiaire changent tous
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: useGetCurrentUserSubscription.getKey({}) }),
        queryClient.invalidateQueries({ queryKey: useGetCurrentUserAnalysisQuotasQuery.getKey() }),
        queryClient.invalidateQueries({ queryKey: useGetCurrentUserVeilleQuotasQuery.getKey() }),
        queryClient.invalidateQueries({ queryKey: useGetCurrentUserVestiaireAccessQuery.getKey() }),
        queryClient.invalidateQueries({ queryKey: useGetCanStartFreeTrialQuery.getKey() }),
      ]);
    },
    [selectPlanMutation, queryClient],
  );

  // Map current subscription data
  const currentSubscription = currentSubscriptionData?.getCurrentUserSubscription;
  const usedHadSuscribed = !_.isNil(currentSubscription);
  // Essai affiché uniquement si le serveur le permet (repli : jamais abonné)
  const trialEligible = canTrialData?.getCanStartFreeTrial ?? !usedHadSuscribed;
  const isTrialing = currentSubscription?.isTrialing ?? false;
  // Pendant l'essai, nextRenewalAt = fin d'essai Stripe
  const trialDaysLeft =
    isTrialing && currentSubscription?.plan?.nextRenewalAt
      ? Math.max(0, Math.ceil((Date.parse(currentSubscription.plan.nextRenewalAt) - Date.now()) / 86_400_000))
      : 0;
  const currentSubscriptionPlanId = currentSubscription?.plan?.id;

  // Changement de plan observé par le sondage (retour de Stripe, résiliation, webhook) : l'accès
  // Vestiaire suit le plan, relu comme après `selectPlanDuringTrial`. `undefined` tant que la
  // réponse n'est pas arrivée, `null` sans plan.
  const observedPlanId = currentSubscriptionData === undefined ? undefined : (currentSubscriptionPlanId ?? null);
  const previousPlanIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (vestiaireAccessNeedsRefresh(previousPlanIdRef.current, observedPlanId)) {
      void queryClient.invalidateQueries({ queryKey: useGetCurrentUserVestiaireAccessQuery.getKey() });
    }
    if (observedPlanId !== undefined) previousPlanIdRef.current = observedPlanId;
  }, [observedPlanId, queryClient]);
  const currentSubscriptionPeriodicity =
    currentSubscription && currentSubscription.plan
      ? mapBackendPeriodicityToFrontend(currentSubscription.plan.periodicity)
      : null;

  // Map plans data
  const subscriptionPlans = useMemo<SubscriptionPlansData | null>(() => {
    if (!plansData?.getAvailableSubscriptionPlans) return null;

    // Find classic and pro plans
    const classicPlanData = plansData.getAvailableSubscriptionPlans.find((p) => p.id === 'recop-classic');
    const proPlanData = plansData.getAvailableSubscriptionPlans.find((p) => p.id === 'recop-pro');

    if (!classicPlanData || !proPlanData) return null;

    // Find custom plans (all plans that are not classic or pro)
    const customPlansData = plansData.getAvailableSubscriptionPlans.filter(
      (p) => p.id !== 'recop-beginner' && p.id !== 'recop-classic' && p.id !== 'recop-pro',
    );

    // Map plans data
    const beginnerPlan = mapBackendSubscriptionPlanToData({
      plan: plansData.getAvailableSubscriptionPlans.find((p) => p.id === 'recop-beginner')!,
      currentPlanId: currentSubscriptionPlanId,
      usedHadSuscribed: !trialEligible,
      billingPeriod,
      currentPlanBillingPeriod: currentSubscriptionPeriodicity,
      handleSubscribe,
      handleCancel,
    });

    const classicPlan = mapBackendSubscriptionPlanToData({
      plan: classicPlanData,
      currentPlanId: currentSubscriptionPlanId,
      usedHadSuscribed: !trialEligible,
      billingPeriod,
      currentPlanBillingPeriod: currentSubscriptionPeriodicity,
      handleSubscribe,
      handleCancel,
    });

    const proPlan = mapBackendSubscriptionPlanToData({
      plan: proPlanData,
      currentPlanId: currentSubscriptionPlanId,
      usedHadSuscribed: !trialEligible,
      billingPeriod,
      currentPlanBillingPeriod: currentSubscriptionPeriodicity,
      handleSubscribe,
      handleCancel,
    });

    const customPlans = customPlansData.map((customPlanData) =>
      mapBackendSubscriptionPlanToData({
        plan: customPlanData,
        currentPlanId: currentSubscriptionPlanId,
        usedHadSuscribed: !trialEligible,
        billingPeriod,
        currentPlanBillingPeriod: currentSubscriptionPeriodicity,
        handleSubscribe,
        handleCancel,
      }),
    );

    return {
      beginner: beginnerPlan,
      classic: classicPlan,
      pro: proPlan,
      customPlans,
    };
  }, [
    plansData,
    currentSubscriptionPlanId,
    trialEligible,
    currentSubscriptionPeriodicity,
    billingPeriod,
    handleSubscribe,
    handleCancel,
  ]);

  return {
    subscriptionPlans,
    currentSubscriptionPeriodicity,
    loading: plansLoading || subscriptionLoading,
    error: null,
    // Essai gratuit (chantier pricing 2026-08-17)
    canStartFreeTrial: trialEligible,
    isTrialing,
    trialDaysLeft,
    selectPlanDuringTrial,
    isSelectingPlan: selectPlanMutation.isPending,
  };
};
